/**
 * Product Recommendations Section
 * Displays recommended products based on configurable data sources
 */

import type { ShopifyProduct } from '../../types/shopify';
import type { RecommendationsSettings } from '../../types/settings';
import { SettingsParser } from '../../types/settings';
import { eventBus } from '../../types/events';
import { qs, qsa, formatMoney } from '../../utils/dom';
import {
  RecommendationSourceFactory,
  type RecommendationConfig,
} from './sources';

export class ProductRecommendations {
  private settings: SettingsParser<RecommendationsSettings>;
  private products: ShopifyProduct[] = [];
  private isLoading = false;

  constructor(private container: HTMLElement) {
    this.settings = new SettingsParser(container);
    this.init();
  }

  private async init(): Promise<void> {
    await this.loadRecommendations();
    this.render();
    this.setupEventListeners();
  }

  private async loadRecommendations(): Promise<void> {
    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoading();

    try {
      const sourceType = this.settings.get('source_type', 'trending');
      const source = RecommendationSourceFactory.getSource(sourceType);

      if (!source) {
        throw new Error(`Unknown source type: ${sourceType}`);
      }

      const config = this.buildConfig();
      this.products = await source.fetch(config);

      // Limit to max products
      const maxProducts = this.settings.get('max_products', 4);
      this.products = this.products.slice(0, maxProducts);

      eventBus.dispatch('recommendations:load', {
        products: this.products,
        source: sourceType,
      });
    } catch (error) {
      console.error('Failed to load recommendations:', error);
      this.showError();
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  private buildConfig(): RecommendationConfig {
    const config: RecommendationConfig = {
      maxProducts: this.settings.get('max_products', 4),
    };

    // Product ID (from current product if on product page)
    const productId = this.container.dataset.productId;
    if (productId) {
      config.productId = parseInt(productId, 10);
    }

    // Product handles (comma-separated)
    const handlesStr = this.settings.get('product_handles');
    if (handlesStr && typeof handlesStr === 'string') {
      config.handles = handlesStr.split(',').map((h) => h.trim());
    }

    // Metafield config
    const namespace = this.settings.get('metafield_namespace');
    const key = this.settings.get('metafield_key');
    if (namespace && key) {
      config.metafieldNamespace = namespace;
      config.metafieldKey = key;
    }

    return config;
  }

  private setupEventListeners(): void {
    // Quick view buttons
    const quickViewBtns = qsa('[data-quick-view]', this.container);
    quickViewBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const productId = (btn as HTMLElement).dataset.productId;
        if (productId) {
          this.openQuickView(parseInt(productId, 10));
        }
      });
    });

    // Add to cart buttons
    const addToCartBtns = qsa('[data-rec-add-to-cart]', this.container);
    addToCartBtns.forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = (btn as HTMLElement).dataset.variantId;
        if (variantId) {
          await this.addToCart(parseInt(variantId, 10));
        }
      });
    });
  }

  private render(): void {
    const productsContainer = qs('[data-recommendations-grid]', this.container);
    const heading = this.settings.get('heading', 'You might also like');
    const headingEl = qs('[data-recommendations-heading]', this.container);

    if (headingEl) {
      headingEl.textContent = heading;
    }

    if (!productsContainer) return;

    if (this.products.length === 0) {
      this.container.style.display = 'none';
      return;
    }

    this.container.style.display = 'block';
    productsContainer.innerHTML = this.products
      .map((product) => this.renderProductCard(product))
      .join('');

    // Re-setup listeners after render
    this.setupEventListeners();
  }

  private renderProductCard(product: ShopifyProduct): string {
    const price = formatMoney(product.price);
    const comparePrice = product.variants[0]?.compare_at_price
      ? formatMoney(product.variants[0].compare_at_price)
      : null;

    const image = product.featured_image?.src || product.images[0]?.src;
    const defaultVariant = product.variants[0];

    return `
      <div class="recommendation-card" data-product-id="${product.id}">
        <a href="${product.url}" class="recommendation-card__link">
          <div class="recommendation-card__image">
            ${
              image
                ? `<img src="${image}" alt="${product.title}" loading="lazy">`
                : '<div class="placeholder-image">No image</div>'
            }
            ${
              !product.available
                ? '<span class="recommendation-card__badge">Sold Out</span>'
                : ''
            }
            ${
              comparePrice
                ? '<span class="recommendation-card__badge recommendation-card__badge--sale">Sale</span>'
                : ''
            }
          </div>
          <div class="recommendation-card__content">
            <h3 class="recommendation-card__title">${product.title}</h3>
            <div class="recommendation-card__price">
              ${
                comparePrice
                  ? `<span class="recommendation-card__price--compare">${comparePrice}</span>`
                  : ''
              }
              <span class="recommendation-card__price--current">${price}</span>
            </div>
          </div>
        </a>
        <div class="recommendation-card__actions">
          ${
            product.available && defaultVariant
              ? `
            <button 
              class="recommendation-card__add-to-cart"
              data-rec-add-to-cart
              data-variant-id="${defaultVariant.id}"
              aria-label="Add ${product.title} to cart">
              Quick Add
            </button>
          `
              : `
            <button 
              class="recommendation-card__add-to-cart" 
              disabled>
              Unavailable
            </button>
          `
          }
        </div>
      </div>
    `;
  }

  private showLoading(): void {
    const loadingEl = qs('[data-recommendations-loading]', this.container);
    if (loadingEl) {
      loadingEl.classList.remove('hidden');
    }
  }

  private hideLoading(): void {
    const loadingEl = qs('[data-recommendations-loading]', this.container);
    if (loadingEl) {
      loadingEl.classList.add('hidden');
    }
  }

  private showError(): void {
    const errorEl = qs('[data-recommendations-error]', this.container);
    if (errorEl) {
      errorEl.classList.remove('hidden');
    }
  }

  private openQuickView(productId: number): void {
    // Dispatch event for quick view
    const product = this.products.find((p) => p.id === productId);
    if (product) {
      console.log('Quick view:', product);
      // Could open a modal with product details
    }
  }

  private async addToCart(variantId: number): Promise<void> {
    try {
      // Use cart drawer if available
      if ((window as any).cartDrawer) {
        await (window as any).cartDrawer.addItem(variantId, 1);
      } else {
        // Fallback to form submission
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/cart/add';

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'id';
        input.value = variantId.toString();

        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
    }
  }

  /**
   * Public API: Reload recommendations
   */
  public async reload(): Promise<void> {
    await this.loadRecommendations();
    this.render();
  }

  /**
   * Public API: Get loaded products
   */
  public getProducts(): ShopifyProduct[] {
    return [...this.products];
  }
}

// Auto-initialize
document.addEventListener('DOMContentLoaded', () => {
  const containers = qsa<HTMLElement>('[data-product-recommendations]');

  containers.forEach((container) => {
    new ProductRecommendations(container);
  });
});
